import { Alert, AlertDescription } from '@zond/ui';
import { cn } from '@zond/utils';
import { AlertCircle, Loader2 } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { extractEmails } from '../utils/email-utils';

interface FileUploadProps {
  onEmailsExtracted: (emails: string[]) => void;
  className?: string;
}

interface FileError {
  title: string;
  description: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const FileUpload: React.FC<FileUploadProps> = ({ onEmailsExtracted, className }) => {
  const { t } = useTranslation('settings');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<FileError | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileRead = async (file: File) => {
    resetState();
    setIsLoading(true);

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError({
        title: t('members.fileUpload.errors.tooLargeTitle'),
        description: t('members.fileUpload.errors.tooLargeDescription', {
          size: MAX_FILE_SIZE / 1024 / 1024,
        }),
      });
      setIsLoading(false);
      return;
    }

    try {
      let fileContent = '';

      if (file.name.endsWith('.csv')) {
        fileContent = await file.text();
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data);

        if (workbook.SheetNames.length === 0) {
          throw new Error(t('members.fileUpload.errors.excelEmpty'));
        }

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        // Convert all cells to text
        const jsonData = XLSX.utils.sheet_to_json<Array<string | number>>(worksheet, { header: 1 });
        fileContent = jsonData.map((row) => (Array.isArray(row) ? row.join(';') : String(row))).join('\n');
      } else {
        throw new Error(t('members.fileUpload.errors.unsupportedFormat'));
      }

      // Use unified method for email extraction for all formats
      const emails = extractEmails(fileContent, false);

      if (emails.length === 0) {
        setError({
          title: t('members.fileUpload.errors.noEmailsTitle'),
          description: t('members.fileUpload.errors.noEmailsDescription'),
        });
        return;
      }

      onEmailsExtracted(emails);
    } catch (error) {
      console.error('Error reading file:', error);
      setError({
        title: t('members.fileUpload.errors.readErrorTitle'),
        description: error instanceof Error ? error.message : t('members.fileUpload.errors.unknownError'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileRead(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <span className={cn('inline-flex items-center gap-1', className)}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv,.xlsx,.xls"
          className="hidden"
        />
        <button
          type="button"
          onClick={handleClick}
          disabled={isLoading}
          className="text-primary hover:underline disabled:opacity-50 disabled:no-underline"
        >
          {isLoading ? (
            <>
              <Loader2 className="inline h-3 w-3 animate-spin" />
              <span className="ml-1">{t('members.fileUpload.uploading')}</span>
            </>
          ) : (
            <>
              {t('members.fileUpload.uploadLink')}
              <span className="text-muted-foreground font-normal">{t('members.fileUpload.formats')}</span>
            </>
          )}
        </button>
      </span>

      {error && (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            <strong>{error.title}:</strong> {error.description}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
